import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Users, Sparkles, HeartHandshake, Landmark, ArrowRight } from "lucide-react";
import { fetchGroups } from "@/lib/api";
import { useLightbox } from "@/components/Lightbox";
import JoinModal from "@/components/JoinModal";

// Local SCC patron portraits bundled with the frontend. Used only when the admin
// hasn't uploaded a group image yet, they render as fallback defaults on the
// directory so /groups looks complete out of the box.
const SCC_LOCAL_IMAGES = {
    "our-lady-of-mt-carmel":         "/images/scc/scc-01.jpg",
    "emmanuel-scc":                  "/images/scc/scc-02.jpg",
    "st-lawrence":                   "/images/scc/scc-03.jpg",
    "st-john-of-god":                "/images/scc/scc-04.jpg",
    "st-joseph-the-worker":          "/images/scc/scc-05.jpg",
    "st-monica":                     "/images/scc/scc-06.jpg",
    "our-lady-of-assumption":        "/images/scc/scc-07.jpg",
    "st-francis-of-assisi":          "/images/scc/scc-08.jpg",
    "st-faustina":                   "/images/scc/scc-09.jpg",
    "st-alphonsus-de-liguori":       "/images/scc/scc-10.jpg",
    "blessed-sr-irene":              "/images/scc/scc-11.jpg",
    "st-theresa-of-the-child-jesus": "/images/scc/scc-12.jpg",
    "st-joseph-allamano":            "/images/scc/scc-13.jpg",
    "st-padre-pio":                  "/images/scc/scc-14.jpg",
    "our-lady-of-fatima":            "/images/scc/scc-15.jpg",
};

const CATEGORIES = [
    { key: "scc",            label: "Small Christian Communities", icon: Sparkles,        accent: "bg-gold-400",
      eyebrow: "SCC · 20 Communities",
      subtitle: "Our Small Christian Communities gather mostly in homes for prayer, formation and mission, the parish's grassroots faith cells." },
    { key: "ecclesiastical", label: "Ecclesiastical Groups",       icon: HeartHandshake,  accent: "bg-maroon-600",
      eyebrow: "Ecclesiastical Groups",
      subtitle: "Parish-level associations, sodalities and ministries serving the liturgy, catechesis and community life." },
    { key: "youth",          label: "Youth Groups",                icon: Users,           accent: "bg-gold-400",
      eyebrow: "Youth & Children",
      subtitle: "From the smallest disciples to young adults, vibrant youth communities forming the next generation of the Church." },
    { key: "ministry",       label: "Ministries",                   icon: Landmark,        accent: "bg-gold-600",
      eyebrow: "Parish Ministries",
      subtitle: "Coordinating teams that animate the pastoral life of the parish across liturgy, family, youth, evangelisation, justice and outreach." },
];

/*
 * GroupTile, one entry inside a category section.
 * Layout mirrors the calm rhythm of Mass Times entries: no card chrome, just
 * a circle image + name + (SCC-only) bio, sitting on the section background.
 *
 * Click targets are separated so they never conflict:
 *   image circle button  → opens the site lightbox
 *   name / See Details    → navigates to /groups/:slug
 *   Join                   → opens the join modal (non-ministry only)
 */
const GroupTile = ({ g, imageSrc, onImageClick, onJoin }) => {
    const isScc = g.category === "scc";
    const isMinistry = g.category === "ministry";
    const ctaLabel = isMinistry ? "Know More" : "See Details";
    // Show a circle image whenever we have one (regardless of category). For
    // SCCs without an admin upload, SCC_LOCAL_IMAGES supplies a bundled
    // portrait. For eccl/youth/ministry with an admin upload, imageSrc is
    // set. Otherwise the tile is name-only.
    const showCircle = !!imageSrc;
    // Keep the SCC letter fallback if a slug ever lacks a bundled image.
    const showLetterFallback = isScc && !imageSrc;

    return (
        <div
            data-testid={`group-card-${g.slug}`}
            className="flex flex-col items-center text-center"
        >
            {showCircle && (
                <button
                    type="button"
                    onClick={() => onImageClick(g.slug)}
                    aria-label={`Open full image of ${g.name}`}
                    data-testid={`group-image-btn-${g.slug}`}
                    className="rounded-full overflow-hidden flex items-center justify-center transition-transform hover:scale-[1.03] cursor-zoom-in"
                    style={{
                        width: "clamp(110px, 30vw, 140px)",
                        height: "clamp(110px, 30vw, 140px)",
                        background: "#1f1f1f",
                        border: "2px solid rgba(212, 175, 55, 0.55)",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    }}
                >
                    <img
                        src={imageSrc}
                        alt={isScc
                            ? `${g.name}, patron saint of ${g.name} Small Christian Community, Holy Trinity Kileleshwa`
                            : `${g.name}, Holy Trinity Kileleshwa`}
                        loading="lazy"
                        className="w-full h-full"
                        style={{ objectFit: "cover", objectPosition: "top center" }}
                    />
                </button>
            )}
            {showLetterFallback && (
                <div
                    className="rounded-full flex items-center justify-center"
                    style={{
                        width: "clamp(110px, 30vw, 140px)",
                        height: "clamp(110px, 30vw, 140px)",
                        background: "#1f1f1f",
                        border: "2px solid rgba(212, 175, 55, 0.55)",
                    }}
                    aria-hidden
                >
                    <span className="font-serif text-3xl" style={{ color: "#D4AF37" }}>
                        {(g.name || "?").slice(0, 1)}
                    </span>
                </div>
            )}

            <Link
                to={`/groups/${g.slug}`}
                data-testid={`group-link-${g.slug}`}
                className={`${(showCircle || showLetterFallback) ? "mt-4" : "mt-0"} font-serif text-base sm:text-lg leading-snug hover:underline`}
                style={{ color: "#FFFFFF" }}
            >
                {g.name}
            </Link>

            {isScc && g.description && (
                <p
                    className="mt-2 text-xs sm:text-sm leading-relaxed max-w-[26ch]"
                    style={{ color: "#CCCCCC" }}
                >
                    {g.description}
                </p>
            )}

            <Link
                to={`/groups/${g.slug}`}
                data-testid={`group-cta-${g.slug}`}
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest hover:opacity-80"
                style={{ color: "#D4AF37" }}
            >
                {ctaLabel} <ArrowRight size={11} />
            </Link>

            {!isMinistry && (
                <button
                    type="button"
                    onClick={() => onJoin(g.name)}
                    data-testid={`group-join-btn-${g.slug}`}
                    aria-label={`Join ${g.name}`}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider underline-offset-4 hover:underline"
                    style={{ color: "#CCCCCC" }}
                >
                    <UserPlus size={12} /> Join
                </button>
            )}
        </div>
    );
};

/*
 * Section, styled after MassTimes.Section — small accent dot, serif title,
 * gold divider, calm content underneath. Adapted for the dark theme.
 */
const Section = ({ meta, groups, onImageClick, onJoin }) => (
    <div
        data-testid={`groups-category-${meta.key}`}
        className="rounded-2xl p-6 shadow-sm"
        style={{ background: "var(--htcc-surface)", border: "1px solid rgba(212, 175, 55, 0.15)" }}
    >
        <div className="flex items-center gap-3">
            <div className={`h-2 w-2 rounded-full ${meta.accent || "bg-gold-400"}`} />
            <h4 className="font-serif text-xl" style={{ color: "#FFFFFF" }}>{meta.label}</h4>
        </div>
        <div className="gold-divider my-3 opacity-50" />
        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#D4AF37" }}>
            {meta.eyebrow}
        </p>
        <p className="mt-2 text-sm max-w-3xl" style={{ color: "#CCCCCC" }}>
            {meta.subtitle}
        </p>
        <div className="mt-5">
            {groups.length === 0 ? (
                <p className="italic text-sm" style={{ color: "#CCCCCC" }}>No groups in this category yet.</p>
            ) : (
                <div className="grid gap-x-4 gap-y-8 grid-cols-2 lg:grid-cols-4">
                    {groups.map((g) => (
                        <GroupTile
                            key={g.id}
                            g={g}
                            imageSrc={g.image_url || (g.category === "scc" ? SCC_LOCAL_IMAGES[g.slug] : null)}
                            onImageClick={onImageClick}
                            onJoin={onJoin}
                        />
                    ))}
                </div>
            )}
        </div>
    </div>
);

const Groups = () => {
    const [groups, setGroups]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [joinModal, setJoinModal] = useState(null);

    useEffect(() => {
        let alive = true;
        fetchGroups()
            .then((d) => alive && setGroups(d))
            .catch(() => { /* stays empty */ })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const byCategory = useMemo(() => {
        const map = { scc: [], ecclesiastical: [], youth: [], ministry: [] };
        for (const g of groups) if (map[g.category]) map[g.category].push(g);
        for (const k of Object.keys(map)) map[k].sort((a, b) => a.name.localeCompare(b.name));
        return map;
    }, [groups]);

    // Build the lightbox items list from every tile that has an image, in the
    // exact order they will render (SCC first, then ecclesiastical, youth,
    // ministry). Ministries don't render images so they contribute nothing.
    const { items, indexBySlug } = useMemo(() => {
        const items = [];
        const indexBySlug = {};
        for (const key of ["scc", "ecclesiastical", "youth", "ministry"]) {
            for (const g of byCategory[key] || []) {
                const src = g.image_url || (g.category === "scc" ? SCC_LOCAL_IMAGES[g.slug] : null);
                if (!src) continue;
                indexBySlug[g.slug] = items.length;
                items.push({
                    src,
                    alt: g.category === "scc"
                        ? `${g.name}, patron saint of ${g.name} Small Christian Community, Holy Trinity Kileleshwa`
                        : `${g.name}, Holy Trinity Kileleshwa`,
                });
            }
        }
        return { items, indexBySlug };
    }, [byCategory]);

    const { open, LightboxNode } = useLightbox(items);
    const handleImageClick = (slug) => {
        const idx = indexBySlug[slug];
        if (idx !== undefined) open(idx);
    };

    return (
        <div className="min-h-screen">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14" data-testid="groups-page">
                <span className="eyebrow" style={{ color: "#D4AF37" }}>Parish Life</span>
                <h1 className="heading-section mt-1" style={{ color: "#FFFFFF" }}>Church Groups &amp; Missions</h1>
                <p className="mt-3 max-w-3xl text-base" style={{ color: "#CCCCCC" }}>
                    We are a home for all.
                </p>

                {loading ? (
                    <p className="mt-10 italic" style={{ color: "#CCCCCC" }}>Loading groups…</p>
                ) : (
                    <div className="mt-8 grid gap-6">
                        {CATEGORIES.map((meta) => (
                            <Section
                                key={meta.key}
                                meta={meta}
                                groups={byCategory[meta.key] || []}
                                onImageClick={handleImageClick}
                                onJoin={setJoinModal}
                            />
                        ))}
                    </div>
                )}
            </div>

            {LightboxNode}
            <JoinModal open={!!joinModal} onClose={() => setJoinModal(null)} groupName={joinModal || ""} />
        </div>
    );
};

export default Groups;
